import {FormEnvironment} from "@fd/Types.ts";
import {FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {SubviewTab} from "@t/ui/SubView.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {ObjectAccess} from "@t/security/ObjectAccess.ts";
import {VRoute} from "@/router/virtual/VirtualRouter.ts";
import {computed, Ref} from "vue";

export type SourceTypeBundle = {
    icon?: string,
    create?: string,
    filter?: string,
    delete?: string,
    deleteConfirm?: string
};

export type SourceType = {
    id:number,
    subId?:number,
    fulltextCode?:string,
    form?:string,
	/** Endpoint to load an existing object. Should contain {id} placeholder */
    edit?:string,
    init?:string,
    create?:string,
    comments?:string,
    files?:string,
	/** Change log endpoint */
    changeLog?:string,
	actionUrls?:Record<string, string>,
    meta?:boolean,
    bundle?: SourceTypeBundle,
    fileAsProperty?:string,
    idProperty?:string,	/* Defaults to "id" */
	appContext?:string[],	/* Optional list of supported app context parameters/arguments by this src type */
	subviews?: SubviewTab[]
};

// Known source types
export const SrcType = {
    TASK: 1,
    EVENT: 2,
    EMAIL: 3,
    OPPORTUNITY: 4,
    BUSCASE: 5,
    CONTACT: 6,
    PARTY: 7,
    PROBLEM: 9,
    USER: 10,
    INVOICE: 11,
    PRODUCT: 12,
    CONTENT: 13,
    OFFER: 14,
    TEMPLATE: 15,
    BRANCH: 16,
    STORE: 17,
    MOTION: 18,
    SMS: 19,
    GIFT: 20,
    BUSCASE_ITEM: 21,
    OFFER_ITEM: 22,
    TRANSFER: 23,
    ATTFILE: 24,
    REPEATED_EVENT: 25,
    CAMPAIGN: 26,
	ATTFILE_ATTACH: 27,
	COMMENT: 28,
    KNOWLEDGE: 29,
    BULK_EMAIL: 30,
    RELATION: 31,
    CONSENT: 32,
    FACT: 33,
	POSTCODE: 34,
	BULK_SMS: 35,
	REGION: 36,
	DYNAMIC_ATTRIBUTE: 37,
	PREF: 38,
	FAVORITE_MAIL: 39,
	MAIL_ACCOUNT: 40,
	WORKSHEET_SETTING: 41,
	WORKSHEET: 42,
	SMS_ACCOUNT: 43,
	LOGINFO: 44,
	CALDAVSETTINGS: 45,
	INTEGRATIONSETTINGS: 46,
	LIST:47,
	GROUP:48,
	ATTFOLDER:49,
	FOLDERMAPPINGS:50,
	MAIL_ACTION:51,
	
	ACCDOC:52,
    MONEY_TRANSACTION:53,
    MONEY_ACCOUNT:54,
    PAYMENT:55,
    BUSCASETYPE:56,
	PROCESS:57,
	TARGET_AUDIENCE:58,
	LIST_ITEM:59,
	WARNING:60,
	FORCE_INFO:61,
	MAIL_ROUTE: 62,
	CAMPAIGN_PARTY:63,
	PROCESS_DEFINITION:64,	// definition of a process
	PROCESS_INSTANCE:65,		// running instance
	PROCESS_TOKEN:66,			// execution point (token)
	PROCESS_VAR:67,			// process variable (data)

	/** Special non-rowobject types */
	SUBVIEW: 500
} as const;

export type SrcType = typeof SrcType[keyof typeof SrcType];

/** Replace {id} placeholder in endpoint string (useful for SourceType.edit endpoint) */
export function setEndpointId(endpoint: string, id: number | string):string {
	return endpoint.replace("{id}", id.toString());
}


export async function loadSourceType(env: FormEnvironment, srcType: number): Promise<SourceType> {
	let allTypes = await env.sourceTypes();
	for (const type of allTypes) {
		if (type.id === srcType) return type;
	}
	throw new Error("Unknown source type " + srcType);
}

export async function loadFormDefinition(env: FormEnvironment, formName: string): Promise<InterfaceRoot<FormDefinition>> {
	let endpoint = "/api/forms/" + formName;
	let resp = await env.net.fetch(endpoint);
	if (resp.ok) return resp.json();
	throw new Error("Cannot load form " + formName);
}


/** Object can implement this interface to be identified */
export interface WithType {
	_type: number;
	_subType?: number;
}

export function isOfType(obj: any, type: number, subType?: number):boolean {
	if (typeof obj !== "object") return false;
	if (obj._type !== type) return false;
	if (subType && obj._subType !== subType) return false;
	return true;
}

export function srcTypeOf(obj:any):number {
	if (typeof obj !== "object") return 0;
	return obj._type ?? 0;
}

export interface RowObjectType {
	type: number;
	subType?: number;
	id?: number|string;
	params?: Record<string, string|number>;
}

/** Object referencing pointer */
export interface RowObjectRef extends RowObjectType {
	id: number|string;
}

/** Object */
export type RowObjectInit = RowObjectType;

export function withIdentity(rot:RowObjectType, id:string|number|null):RowObjectRef {
	if (id == null) return { ...rot, id: 0 };
	return { ...rot, id: id };
}

export function isRef(rot:RowObjectType):rot is RowObjectRef {
	return rot.type > 0 && !!rot.id;
}

/** Test if the reference points to an object with identity (exists in the DB and has primary key) */
export function hasIdentity(rot:RowObjectType):rot is RowObjectRef {
	if (!isRef(rot)) return false;
	if (typeof rot.id === 'string') return rot.id.length > 0 && rot.id !== "0";
	return rot.id !== 0;
}

/** Return true if both reference the same row object */
export function sameRowObject(ro1: RowObjectType, ro2: RowObjectType):boolean {
	if (ro1 == null || ro2 == null) return false;
	if (ro1.type != ro2.type) return false;
	if (hasIdentity(ro1)) {
		if (hasIdentity(ro2)) return ro1.id === ro2.id;
		return false;
	} else {
		return !hasIdentity(ro2);
	}
}


function routeQueryParams(route:VRoute, ...exclude:string[]):Record<string, string> {
	const params:Record<string, string> = {};
	for (let [name, value] of Object.entries(route.query)) {
		if (name.startsWith("_") || exclude.indexOf(name) >= 0) continue;
		params[name] = value;
	}
	return params;
}

export function objectRef(route:VRoute, srcType:number):Ref<RowObjectType> {
	return computed<RowObjectType>(() => {
		if (route.name?.startsWith("edit")) {
			return { type: srcType, id: route.query.id, params: routeQueryParams(route, "id") };
		} else {
			return { type: srcType, params: routeQueryParams(route) };
		}
	});
}